import { normalizeFormElementType } from '@/common/utils/formElementType'
import {
  fieldEntryHasPresentableValue,
  fieldEntryLooksEmptyIncoming,
} from '@/common/utils/changedDataFieldMerge'
import { isTextLikeFormElementType } from '@/common/utils/liveFormFieldOverrides'

export function isCollateralFilterFieldKey(key: string): boolean {
  const k = String(key ?? '').trim()
  if (!k) return false
  if (k.includes('__') || k.endsWith('_params')) return false
  return true
}

export function isCollateralScalarFilterFieldEntry(entry: unknown, key: string): boolean {
  if (!isCollateralFilterFieldKey(key)) return false
  if (!fieldEntryHasPresentableValue(entry)) return false
  if (!entry || typeof entry !== 'object' || Array.isArray(entry)) return false
  const type = String((entry as { type?: string }).type ?? '')
  if (type === 'UniDataTable' || type === 'UniDataTableFields') return false
  const val = (entry as { value?: unknown }).value
  if (val && typeof val === 'object' && Array.isArray((val as { rows?: unknown[] }).rows)) {
    return false
  }
  return true
}

/**
 * `resyncTabSlotFields` → refill from page snapshot.
 * TEXT already skipped so the widget is not stomped by sticky empty uniData.
 * Identificator is skipStore: En.refillElement writes lastRefillField, not pagesData.
 * A later resync with opening "" (edit 2873 / WUI-571) would wipe FIAS codes.
 */
const PAGE_DATA_RESYNC_SKIP_REFILL_TYPES = new Set([
  'TEXT',
  'PASSWORD',
  'TEXTAREA',
  'MULTILINETEXT',
  'LISTBOX',
  'COMBOBOX',
  'TABLE',
  'TREETABLE',
  'GROUPTABLE',
  'EDITABLE',
  'IDENTIFICATOR',
])

export function shouldSkipPageDataResyncElementRefill(elementType: unknown): boolean {
  const normalized =
    normalizeFormElementType(elementType) ??
    String(elementType ?? '')
      .trim()
      .toUpperCase()
  return PAGE_DATA_RESYNC_SKIP_REFILL_TYPES.has(normalized)
}

/** skipStore для setValue в refill: committed text/multiselect → pagesData. */
export function resolveRefillSkipStore(
  elementType: string | undefined | null,
  fieldValue: { value?: unknown; type?: string } | undefined,
): boolean {
  const normalizedType = normalizeFormElementType(elementType) ?? String(elementType ?? '')

  if (
    (normalizedType === 'TABLE' ||
      normalizedType === 'TREETABLE' ||
      normalizedType === 'EDITABLE') &&
    fieldValue !== undefined &&
    String(fieldValue.type ?? '') === 'UniDataTable'
  ) {
    // TABLE: getTableItems берёт pageFields при empty:true|false (WUI-563 / listOrders).
    return false
  }

  const isMultiSelectRefill =
    normalizedType === 'MULTISELECT' &&
    fieldValue !== undefined &&
    fieldEntryHasPresentableValue(fieldValue)
  if (isMultiSelectRefill) return false

  const isTextLike = isTextLikeFormElementType(normalizedType)
  const isDateTimeField = normalizedType === 'DATE' || normalizedType === 'DATETIME'
  const commitClearToStore =
    fieldValue !== undefined &&
    (isTextLike || isDateTimeField) &&
    fieldEntryLooksEmptyIncoming(fieldValue)

  if (isTextLike && fieldValue !== undefined && fieldEntryHasPresentableValue(fieldValue)) {
    return false
  }

  return !commitClearToStore
}
